import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CircleCheckBig,
  CircleDashed,
  CircleSlash,
  FileText,
  Flag,
  FlagTriangleRight,
  TrendingDown,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { flightStateOf, type FlightState } from '@/components/CampaignTimeline'
import { TONE_CLASSES, type Tone } from '@/components/DeliveryStatusBadge'
import { RunReportDialog } from '@/components/reports/RunReportDialog'
import {
  formatObjectiveValue,
  primaryObjective,
  type CurrencyAmount,
  type LineItemObjectives,
} from '@/services/lineItems/lineItemsApi'
import type { ReportTarget } from '@/services/reports/reportsApi'
import { formatDate, formatPercent } from '@/utils/format'

/** At or above this whole percent an ended flight reads as over-delivered rather than "in full". */
const OVER_PCT = 110
/** Below 100 but at or above this reads as "just short"; under it, "under target". */
const NEAR_PCT = 90
const DAY_MS = 24 * 60 * 60 * 1000

export type FlightOutcomeKind =
  | 'full'
  | 'over'
  | 'near'
  | 'under'
  | 'nothing'
  | 'noFigure'
  | 'noObjective'

export interface FlightOutcome {
  kind: FlightOutcomeKind
  tone: Tone
  /** Whole-percent delivery, rounded as pacingOf rounds it; null without a figure. */
  pct: number | null
  /** Some delivery, but under half a percent, so pct rounds to 0. */
  underOnePct: boolean
  /** Ended by the ad server before the end date passed. */
  stoppedEarly: boolean
}

const KIND_TONES: Record<FlightOutcomeKind, Tone> = {
  full: 'green',
  over: 'blue',
  near: 'amber',
  under: 'red',
  nothing: 'red',
  noFigure: 'gray',
  noObjective: 'gray',
}

const KIND_ICONS: Record<FlightOutcomeKind, LucideIcon> = {
  full: CircleCheckBig,
  over: TrendingUp,
  near: CircleDashed, // an almost-closed ring
  under: TrendingDown,
  nothing: CircleSlash,
  noFigure: Flag,
  noObjective: Flag,
}

/** Null while the flight has not ended — a live line item shows nothing new. */
export function flightOutcomeOf(
  flight: FlightState,
  deliveryPct: number | null,
  hasObjective: boolean
): FlightOutcome | null {
  if (!flight.ended) return null
  // Ended but the end date has not passed: the ad server stopped it.
  const stoppedEarly = !flight.elapsed
  const pct = deliveryPct === null ? null : Math.round(deliveryPct)
  const kind: FlightOutcomeKind =
    pct === null
      ? hasObjective
        ? 'noFigure'
        : 'noObjective'
      : pct >= OVER_PCT
        ? 'over'
        : pct >= 100 // the same Math.round >= 100 as pacingOf's green fill
          ? 'full'
          : pct >= NEAR_PCT
            ? 'near'
            : (deliveryPct ?? 0) > 0 // raw value: 0.4% is a little delivery, not none
              ? 'under'
              : 'nothing'
  const underOnePct = pct === 0 && (deliveryPct ?? 0) > 0
  return { kind, tone: KIND_TONES[kind], pct, underOnePct, stoppedEarly }
}

/** Delivered as a percent of target, when both are plain counts or money in one currency. */
function ratioPct(
  delivered: number | CurrencyAmount | undefined,
  target: number | CurrencyAmount
): number | null {
  if (delivered == null) return null
  if (typeof delivered === 'number' && typeof target === 'number') {
    return target > 0 ? (delivered / target) * 100 : null
  }
  if (
    typeof delivered === 'object' &&
    typeof target === 'object' &&
    delivered.currency === target.currency &&
    target.amount > 0
  ) {
    return (delivered.amount / target.amount) * 100
  }
  return null
}

export interface FlightOutcomeBandProps {
  startDate: string
  endDate: string
  executionState: string
  deliveryPct?: number | null
  elapsedTimePct?: number | null
  objectives?: LineItemObjectives
  objectiveDelivery?: LineItemObjectives
  /** What the CTA reports on; omit to hide it. */
  reportTarget?: ReportTarget
  className?: string
}

/**
 * How a finished flight went, said in words: a tinted band with the verdict,
 * the delivered figure and a way to the final report. Renders nothing while
 * the flight is still running.
 */
export function FlightOutcomeBand({
  startDate,
  endDate,
  executionState,
  deliveryPct,
  elapsedTimePct,
  objectives,
  objectiveDelivery,
  reportTarget,
  className,
}: FlightOutcomeBandProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const [runOpen, setRunOpen] = useState(false)
  const flight = flightStateOf(startDate, endDate, executionState, elapsedTimePct ?? null)
  const objective = primaryObjective(objectives)
  const delivered = objective ? objectiveDelivery?.[objective.type] : undefined
  const outcome = flightOutcomeOf(
    flight,
    deliveryPct ?? (objective ? ratioPct(delivered, objective.target) : null),
    !!objective
  )
  if (!outcome) return null

  const fmtDate = (value: string) =>
    formatDate(value, { day: 'numeric', month: 'short', year: 'numeric' })
  const Icon = KIND_ICONS[outcome.kind]

  const eyebrow = !endDate
    ? t(outcome.stoppedEarly ? 'flightOutcome.stoppedEarlyUndated' : 'flightOutcome.ended')
    : outcome.stoppedEarly
      ? t('flightOutcome.stoppedEarly', { date: fmtDate(endDate) })
      : t('flightOutcome.endedOn', { date: fmtDate(endDate) })

  const verdictKey =
    outcome.stoppedEarly && (outcome.kind === 'full' || outcome.kind === 'over')
      ? 'fullEarly'
      : outcome.kind
  const verdict = t(`flightOutcome.verdict.${verdictKey}`)

  const parts: string[] = []
  if (objective) {
    const label = t(`lineItems.objectiveTypes.${objective.type}`, { defaultValue: objective.type })
    const target = formatObjectiveValue(objective.target)
    parts.push(
      delivered != null
        ? t('flightOutcome.objective', {
            objective: label,
            delivered: formatObjectiveValue(delivered),
            target,
          })
        : outcome.kind === 'nothing'
          ? t('flightOutcome.objectiveNone', { objective: label, target })
          : t('flightOutcome.objectiveTarget', { objective: label, target })
    )
  } else {
    parts.push(t('flightOutcome.noObjective'))
  }
  if (outcome.kind === 'noFigure' && delivered == null) parts.push(t('flightOutcome.noFigure'))
  // Left out when stopped early: the real stop date is unknown.
  if (!outcome.stoppedEarly && flight.start && flight.end) {
    parts.push(
      t('flightOutcome.ranFor', {
        count: Math.max(1, Math.round((flight.end.getTime() - flight.start.getTime()) / DAY_MS)),
      })
    )
  }
  const detail = parts.join(' · ')

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'mx-(--card-spacing) flex flex-col gap-3 rounded-lg border px-4 py-3.5 sm:flex-row sm:items-center sm:gap-5',
        TONE_CLASSES[outcome.tone],
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          aria-hidden
          className="grid size-10 shrink-0 place-items-center rounded-full bg-(--ad-surface) shadow-xs"
        >
          <Icon className="size-5 text-current" />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="flex items-center gap-1.5 text-xs font-medium tracking-wide text-current uppercase">
            <FlagTriangleRight aria-hidden className="size-3.5" />
            {eyebrow}
          </p>
          <h2 id={headingId} className="text-base leading-snug font-semibold text-balance">
            {verdict}
          </h2>
          <p className="text-xs leading-relaxed text-pretty text-(--ad-text)">{detail}</p>
        </div>
      </div>

      {(outcome.pct !== null || reportTarget) && (
        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-current/15 pt-3 sm:flex-col sm:items-end sm:justify-center sm:gap-2 sm:border-t-0 sm:pt-0">
          {outcome.pct !== null && (
            <span className="text-2xl leading-none font-semibold text-(--ad-text) tabular-nums">
              <span className="sr-only">{t('flightOutcome.figureLabel')} </span>
              {outcome.underOnePct
                ? t('flightOutcome.underOnePct', { value: formatPercent(1, 0) })
                : formatPercent(outcome.pct, 0)}
            </span>
          )}
          {reportTarget && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-(--ad-surface)"
              onClick={() => setRunOpen(true)}
            >
              <FileText aria-hidden className="size-4" />
              {t('flightOutcome.cta')}
            </Button>
          )}
        </div>
      )}
      {reportTarget && (
        <RunReportDialog target={reportTarget} open={runOpen} onOpenChange={setRunOpen} />
      )}
    </section>
  )
}
